import { useBoardPanelDrag } from './useBoardPanelDrag';

import type { ReviewAnnotationColor, ReviewAnnotationTool } from './ReviewAnnotationLayer';

interface ReviewPanelOffset {
  readonly x: number;
  readonly y: number;
}

interface ReviewPanelDragBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

function clampReviewPanelAxis(value: number, minimum: number, maximum: number): number {
  if (minimum > maximum) return (minimum + maximum) / 2;
  return Math.min(maximum, Math.max(minimum, value));
}

export function constrainReviewPanelOffset(
  offset: ReviewPanelOffset,
  bounds: ReviewPanelDragBounds,
): ReviewPanelOffset {
  return {
    x: clampReviewPanelAxis(offset.x, bounds.minX, bounds.maxX),
    y: clampReviewPanelAxis(offset.y, bounds.minY, bounds.maxY),
  };
}

export function canStartReviewPanelDrag(target: EventTarget | null): boolean {
  const closest = (target as { closest?: (selector: string) => unknown } | null)?.closest;
  return (
    typeof closest !== 'function' ||
    closest.call(target, 'button, input, select, textarea') === null
  );
}

export function ReviewAnnotationPanel({
  tool,
  color,
  width,
  annotationCount,
  sharingStage = false,
  draggable = true,
  onTool,
  onColor,
  onWidth,
  onUndo,
  onClear,
  onClose,
}: {
  readonly tool: ReviewAnnotationTool;
  readonly color: ReviewAnnotationColor;
  readonly width: number;
  readonly annotationCount: number;
  readonly sharingStage?: boolean;
  readonly draggable?: boolean;
  readonly onTool: (tool: ReviewAnnotationTool) => void;
  readonly onColor: (color: ReviewAnnotationColor) => void;
  readonly onWidth: (width: number) => void;
  readonly onUndo: () => void;
  readonly onClear: () => void;
  readonly onClose: () => void;
}) {
  const panel = useBoardPanelDrag();
  return (
    <aside
      ref={panel.panelRef}
      className={`review-annotation-panel${sharingStage ? ' is-sharing-stage' : ''}${draggable ? '' : ' is-docked'}`}
      style={draggable ? panel.style : undefined}
      aria-label="棋盘标记工具"
    >
      <header {...(draggable ? panel.dragHandlers : {})}>
        <strong>添加标记</strong>
        {draggable ? <span>拖动此处移动</span> : null}
        {draggable && panel.floating ? (
          <button type="button" onClick={panel.resetPosition}>
            归位
          </button>
        ) : null}
        <button
          type="button"
          aria-label="关闭添加标记窗口"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => {
            panel.resetPosition();
            onClose();
          }}
        >
          关闭
        </button>
      </header>
      <div className="review-annotation-tools" role="group" aria-label="标记类型">
        {(
          [
            ['BRUSH', '🖌'],
            ['LETTER', 'AB'],
            ['TRIANGLE', '△'],
            ['CIRCLE', '〇'],
            ['CROSS', '×'],
          ] as const
        ).map(([nextTool, label]) => (
          <button
            className={tool === nextTool ? 'is-active' : ''}
            type="button"
            key={nextTool}
            aria-pressed={tool === nextTool}
            onClick={() => onTool(nextTool)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="review-annotation-options">
        <span>颜色</span>
        <button
          className={`review-color-button is-red${color === 'RED' ? ' is-active' : ''}`}
          type="button"
          aria-pressed={color === 'RED'}
          onClick={() => onColor('RED')}
        >
          红色
        </button>
        <button
          className={`review-color-button is-blue${color === 'BLUE' ? ' is-active' : ''}`}
          type="button"
          aria-pressed={color === 'BLUE'}
          onClick={() => onColor('BLUE')}
        >
          蓝色
        </button>
      </div>
      {tool === 'BRUSH' ? (
        <label className="review-annotation-width">
          画笔粗细
          <input
            type="range"
            min="3"
            max="15"
            step="2"
            value={width}
            onChange={(event) => onWidth(Number(event.currentTarget.value))}
          />
          <strong>{width}</strong>
        </label>
      ) : null}
      <footer>
        <button type="button" disabled={annotationCount === 0} onClick={onUndo}>
          撤销
        </button>
        <button type="button" disabled={annotationCount === 0} onClick={onClear}>
          清空
        </button>
      </footer>
    </aside>
  );
}
